import React, { useState, useEffect } from 'react';
import { Container, Tab, Header, Button, Icon, Message, Dimmer, Loader } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
// Import the Voice Lab composite data hook
import useVoiceLabCompositeData from '../../hooks/useVoiceLabCompositeData';

// Import the integrated components
import VoiceLabCharacterList from './VoiceLabCharacterList.integrated';
import VoiceLabMyRecordings from './VoiceLabMyRecordings.integrated';

/**
 * Demo component that shows how to integrate the Voice Lab components
 * with the Voice Actor management system
 */
const VoiceLabIntegrationDemo = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);

  // Use the Voice Lab composite data hook to fetch and manage data
  const {
    actorUsers,
    portfolios,
    characters,
    variationsMap,
    isLoading,
    error
  } = useVoiceLabCompositeData();

  // Create a system status object for the info panel
  const systemStatus = {
    initialized: !isLoading && actorUsers.length > 0,
    actorUsersCount: actorUsers.length,
    portfoliosCount: portfolios.length,
    charactersCount: characters.length,
    variationsCount: Object.values(variationsMap || {}).flat().length,
    recordedVariationsCount: Object.values(variationsMap || {})
      .flat()
      .filter(v => v.s3Url)
      .length
  };

  // Handle tab changes
  const handleTabChange = (e, { activeIndex }) => {
    setActiveIndex(activeIndex);
  };

  // Define the tabs for the demo
  const panes = [
    {
      menuItem: 'Character Voice Requests',
      render: () => (
        <Tab.Pane>
          <VoiceLabCharacterList />
        </Tab.Pane>
      )
    },
    {
      menuItem: 'My Voice Recordings',
      render: () => (
        <Tab.Pane>
          <VoiceLabMyRecordings />
        </Tab.Pane>
      )
    }
  ];

  // If the system is still loading, show a loading indicator
  if (isLoading) {
    return (
      <Container fluid>
        <Dimmer active>
          <Loader>Initializing Voice Actor System...</Loader>
        </Dimmer>
      </Container>
    );
  }

  // If there was an error, show an error message
  if (error) {
    return (
      <Container fluid>
        <Message negative>
          <Message.Header>Error Initializing Voice Actor System</Message.Header>
          <p>{error}</p>
          <Button primary onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Message>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Header as="h1" dividing style={{ margin: 0, flex: 1 }}>
          <Header.Content>
            Voice Lab Integration Demo
            <Header.Subheader>Demonstrating integration between Voice Lab and Voice Actor Management</Header.Subheader>
          </Header.Content>
        </Header>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon
            labelPosition="left"
            onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTOR-WORKSHEET' } })}
            style={{ marginRight: '10px' }}
          >
            <Icon name="user" />
            Voice Actor Worksheet
          </Button>
          <Button
            icon
            labelPosition="left"
            onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTORS' } })}
          >
            <Icon name="users" />
            Voice Actors List
          </Button>
        </div>
      </div>

      <Message info>
        <Message.Header>Integration Demo</Message.Header>
        <p>
          This demo shows how the Voice Lab components have been integrated with the Voice Actor management system.
          The components now use real data from the Redux store instead of mock data.
        </p>

        <p>
          <strong>Data Hierarchy:</strong>
        </p>
        <ul>
          <li><strong>Portfolio</strong> - Top level container for a voice acting project</li>
          <li><strong>Characters</strong> - Each portfolio contains multiple characters</li>
          <li><strong>Voice Variations</strong> - Each character has multiple voice variations (different emotions, tones, etc.)</li>
        </ul>

        <p>
          The UI now properly reflects this hierarchical structure, showing characters grouped under portfolios,
          and voice variations grouped under their respective characters.
        </p>

        {systemStatus && (
          <div>
            <p>
              <strong>System Status:</strong>
            </p>
            <ul>
              <li>Initialized: {systemStatus.initialized ? 'Yes' : 'No'}</li>
              <li>Actor Users: {systemStatus.actorUsersCount}</li>
              <li>Portfolios: {systemStatus.portfoliosCount}</li>
              <li>Characters: {systemStatus.charactersCount}</li>
              <li>Variations: {systemStatus.variationsCount}</li>
              <li>Recorded Variations: {systemStatus.recordedVariationsCount}</li>
            </ul>
          </div>
        )}
        <p>
          <strong>Key Features:</strong>
        </p>
        <ul>
          <li>Real-time data fetching from the Voice Actor management system</li>
          <li>Consistent recording interface across both systems</li>
          <li>Shared state management using Redux</li>
          <li>Seamless navigation between Voice Lab and Voice Actor Worksheet</li>
        </ul>
      </Message>

      <Tab panes={panes} activeIndex={activeIndex} onTabChange={handleTabChange} />
    </Container>
  );
};

export default VoiceLabIntegrationDemo;
